
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface AdminOrProviderRouteProps {
  children: React.ReactNode;
}

const AdminOrProviderRoute = ({ children }: AdminOrProviderRouteProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const checkAccessStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          toast({
            title: "Authentication Required",
            description: "Please log in to access this page.",
            variant: "destructive",
          });
          navigate('/auth');
          setHasAccess(false);
          return;
        }
        
        // Check if user has admin, strategy_provider, or editor role
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();
        
        if (error) {
          console.error("Error checking access status:", error);
          toast({
            title: "Authorization Error",
            description: "Failed to verify your access status.",
            variant: "destructive",
          });
          navigate('/');
          setHasAccess(false);
          return;
        }
        
        if (data?.role !== 'admin' && data?.role !== 'strategy_provider' && data?.role !== 'editor') {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access this page.",
            variant: "destructive",
          });
          navigate('/');
          setHasAccess(false);
          return;
        }
        
        setUserRole(data?.role);
        setHasAccess(true);
      } catch (error) {
        console.error("Error checking access status:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred.",
          variant: "destructive",
        });
        navigate('/');
        setHasAccess(false);
      }
    };
    
    checkAccessStatus();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/auth');
        setHasAccess(false);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [navigate, toast]);
  
  if (hasAccess === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#517FA4] mb-3"></div>
        <div className="text-[#517FA4] font-medium">Verifying access status...</div>
      </div>
    );
  }
  
  return hasAccess ? <>{children}</> : null;
};

export default AdminOrProviderRoute;
